import { useMutation, useQuery } from '@tanstack/vue-query';
import { useToast } from '@@/packages/shared/components/toast/useToast';
import { ApiError } from '@/api/errors';
import { i18n } from '@/i18n/i18n';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { loadFileContent, hashFileBase64Encoded } from '../utils';
import { useUploadsStore } from '../consolidatorStore';
import type { FileType } from '../types';
import { DOWNLOAD_URL_QUERY } from './queryKeys';

export { fetchUploads, type FilesResponse } from './fetchUploads';

export enum UploadPublicType {
  ProfilePicture = 'profile-pictures',
  Logo = 'logos',
}

type FileDownloadURLResponse = { url: string };
export const fetchFileDownloadURL = (fileID: string) =>
  request<FileDownloadURLResponse>('GET', `/uploads/${fileID}/url`);

export type BeginResponse = {
  id: string;
  url: string;
};

// Must match MinAcceptedFileSize and MaxAcceptedFileSize in
// services/uploads/pkg/uploads/uploads.go.
const MIN_UPLOAD_BYTES = 1;
const MAX_UPLOAD_BYTES = 256 * 1_000_000;

// Surfaces a toast and throws an ApiError-shaped 400 so the upload mutation
// rejects without tripping vue-query's `throwOnError` boundary (see
// plugins/vueQuery.ts), which only re-throws non-ApiError or >=500 responses.
function assertUploadSize(size: number) {
  if (size < MIN_UPLOAD_BYTES || size > MAX_UPLOAD_BYTES) {
    const message = i18n.global.t('uploads.fileSizeInvalid');
    useToast().error(message);
    throw new ApiError(message, { status: 400 });
  }
}

// Categorises an upload server-side (see FileModel.kind). Most callers upload
// generic documents; supplier-coverage submissions tag their files so the
// commercial team can query them later.
type UploadKind = 'document' | 'supplier_coverage';

// Size check, read bytes, hash — the shared prep every upload does before begin.
export async function prepareUpload(file: FileType) {
  assertUploadSize(file.size);
  const content = await loadFileContent(file.fileObject);
  const hash = await hashFileBase64Encoded(content);
  return { content, hash };
}

// PUTs the file bytes to the signed S3 URL from an upload-begin call.
//
// `hash` is the base64 SHA-256 the begin call was given. S3 rejects a presigned
// PUT carrying an `x-amz-*` header the URL did not sign, and only some signer
// versions sign the checksum, so send it back only when the URL asks for it.
export async function putUploadToS3(
  url: string,
  content: Uint8Array,
  contentType: string,
  hash: string,
) {
  const signsChecksum = new URL(url).searchParams
    .get('X-Amz-SignedHeaders')
    ?.split(';')
    .includes('x-amz-checksum-sha256');

  const res = await fetch(url, {
    method: 'PUT',
    body: new Blob([new Uint8Array(content)]),
    headers: {
      ...(contentType ? { 'Content-Type': contentType } : {}),
      ...(signsChecksum ? { 'x-amz-checksum-sha256': hash } : {}),
    },
  });

  if (!res.ok) {
    throw new ApiError(`Upload to S3 failed with status ${res.status}`, { status: res.status });
  }
}

export type UploadFileVariables = { file: FileType; kind?: UploadKind };
type UploadFileResponse = string;
export function useUploadFile(options?: MutationOptions<UploadFileResponse, UploadFileVariables>) {
  const store = useUploadsStore();
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ file, kind = 'document' }) {
        const { content, hash } = await prepareUpload(file);
        // declare upload intent, get upload url
        const res = await request<BeginResponse>('POST', '/uploads/begin', {
          filename: file.filename,
          contentType: file.contentType,
          kind,
          hash,
          fileSize: file.size,
        });

        await putUploadToS3(res.data.url, content, file.contentType, hash);

        return res.data.id;
      },
      onSuccess(id) {
        store.subscribe([id]);
      },
    }),
  );
}

type UploadFilePublicVariables = {
  type: UploadPublicType;
  filename: string;
  contentType: string;
  content: Uint8Array;
  fileSize: number;
};
export function useUploadFilePublic(
  options?: MutationOptions<UploadFileResponse, UploadFilePublicVariables>,
) {
  const store = useUploadsStore();
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ type, filename, contentType, content, fileSize }) {
        assertUploadSize(fileSize);
        const hash = await hashFileBase64Encoded(content);
        // declare upload intent, get upload url
        const res = await request<BeginResponse>('POST', `/uploads/begin/public/${type}`, {
          filename,
          contentType,
          kind: 'document',
          hash,
          fileSize,
        });

        await putUploadToS3(res.data.url, content, contentType, hash);

        return res.data.id;
      },
      onSuccess(id) {
        store.subscribe([id]);
      },
    }),
  );
}

export function useDownloadUrl(uploadID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [DOWNLOAD_URL_QUERY, toRef(uploadID)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchFileDownloadURL(queryKey[1]);
        return res.data.url;
      },
      // Signed download link expires after 5 minutes
      staleTime: 4.5 * 60e3,
      placeholderData: undefined,

      // Refetching is generally bad as it causes a document to be redownloaded
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
  );

  return { ...query, url: query.data };
}
